/**
 * Admin API client functions
 * 
 * Provides type-safe access to site admin endpoints for:
 * - Global dead celebrity list management
 * - Celebrity cache management
 * - Custom celebrity administration
 * 
 * Implements Requirements: 6.1, 6.2, 6.3, 6.4, 6.5, 8.1, 9.1, 10.1, 11.1
 */

import { apiClient } from './client';
import type { 
  ApiResponse, 
  Celebrity, 
  DeathSource, 
  PendingDeathRecord, 
  PendingDeathStatus,
  ScraperRunResult,
  CreateDeathSourceInput,
  UpdateDeathSourceInput,
  ApprovePendingDeathInput,
  RejectPendingDeathInput,
} from './types';

// ============================================================================
// Admin Types
// ============================================================================

/** Global death record - site admin managed */
export interface GlobalDeathRecord {
  celebrityName: string;
  deathDate: string; // ISO 8601 date
  ageAtDeath: number;
  sourceUrl: string;
  addedBy: string; // Site admin userId
  addedAt: string; // ISO 8601 timestamp
}

/** Input for adding a celebrity to the global dead list */
export interface AddGlobalDeathInput {
  celebrityName: string;
  deathDate: string; // ISO 8601 date (YYYY-MM-DD)
  ageAtDeath: number;
  sourceUrl: string;
}

/** Result of adding to global dead list */
export interface AddGlobalDeathResult {
  record: GlobalDeathRecord;
  poolsUpdated: number;
  playersAffected: number;
}

/** Options for listing global deaths */
export interface ListGlobalDeathsOptions {
  limit?: number;
  offset?: number;
}

/** Paginated result for global death listing */
export interface PaginatedGlobalDeaths {
  records: GlobalDeathRecord[];
  total: number;
  limit: number;
  offset: number;
}

/** Input for updating a global death record */
export interface UpdateGlobalDeathInput {
  deathDate?: string; // ISO 8601 date (YYYY-MM-DD)
  ageAtDeath?: number;
  sourceUrl?: string;
}

/** Result of updating a global death record */
export interface UpdateGlobalDeathResult {
  record: GlobalDeathRecord;
  poolsUpdated: number;
  playersAffected: number;
}

/** Input for updating a custom celebrity */
export interface UpdateCustomCelebrityInput {
  name?: string;
  birthday?: string; // ISO 8601 date (YYYY-MM-DD)
  description?: string;
  gender?: string;
  nationality?: string;
  occupation?: string[];
}

/** Input for merging custom celebrities */
export interface MergeCelebritiesInput {
  sourceName: string;
  targetName: string;
  overrideDetails?: Partial<UpdateCustomCelebrityInput>;
}

/** Result of merging custom celebrities */
export interface MergeCelebritiesResult {
  mergedCelebrity: CustomCelebrityWithStats;
  picksUpdated: number;
  deathRecordsUpdated: number;
}

/** Custom celebrity with usage stats for admin listing */
export interface CustomCelebrityWithStats extends Celebrity {
  description: string;
  createdBy: string;
  createdAt: string;
  isCustom: true;
  pickCount: number;
  poolIds: string[];
}

/** Options for listing custom celebrities */
export interface ListCustomCelebritiesOptions {
  limit?: number;
  offset?: number;
  sortBy?: 'name' | 'createdAt' | 'pickCount';
  sortOrder?: 'asc' | 'desc';
  searchQuery?: string;
}

/** Paginated result for custom celebrity listing */
export interface PaginatedCustomCelebrities {
  celebrities: CustomCelebrityWithStats[];
  total: number;
  limit: number;
  offset: number;
}

// ============================================================================
// Global Dead Celebrity List Management
// ============================================================================

/**
 * Add a celebrity to the global dead list
 * Creates death records in all active pools where the celebrity is picked
 * 
 * POST /admin/dead-celebrities
 * Implements Requirement 6.1
 * 
 * @param input - Celebrity death information
 * @returns Result with record and affected counts
 */
export function addGlobalDeath(
  input: AddGlobalDeathInput
): Promise<ApiResponse<AddGlobalDeathResult>> {
  return apiClient.post<AddGlobalDeathResult>('/admin/dead-celebrities', input);
}

/**
 * Remove a celebrity from the global dead list
 * Does NOT remove pool-specific death records
 * 
 * DELETE /admin/dead-celebrities/{celebrityName}
 * Implements Requirement 6.2
 * 
 * @param celebrityName - Name of the celebrity to remove
 */
export function removeGlobalDeath(
  celebrityName: string
): Promise<ApiResponse<void>> {
  const encodedName = encodeURIComponent(celebrityName);
  return apiClient.delete<void>(`/admin/dead-celebrities/${encodedName}`);
}

/**
 * List celebrities in the global dead list with pagination
 *
 * GET /admin/dead-celebrities
 * Implements Requirement 6.3
 *
 * @param options - Pagination options (limit, offset)
 * @returns Paginated list of global death records with total count
 */
export function listGlobalDeaths(
  options: ListGlobalDeathsOptions = {}
): Promise<ApiResponse<PaginatedGlobalDeaths>> {
  const params = new URLSearchParams();

  if (options.limit !== undefined) {
    params.set('limit', options.limit.toString());
  }
  if (options.offset !== undefined) {
    params.set('offset', options.offset.toString());
  }

  const queryString = params.toString();
  const endpoint = queryString
    ? `/admin/dead-celebrities?${queryString}`
    : '/admin/dead-celebrities';

  return apiClient.get<PaginatedGlobalDeaths>(endpoint);
}

/**
 * Update a global death record
 * Recalculates all affected pool scores
 *
 * PUT /admin/dead-celebrities/{celebrityName}
 *
 * @param celebrityName - Name of the celebrity to update
 * @param input - Fields to update (deathDate, ageAtDeath, sourceUrl)
 * @returns Updated record and affected counts
 */
export function updateGlobalDeath(
  celebrityName: string,
  input: UpdateGlobalDeathInput
): Promise<ApiResponse<UpdateGlobalDeathResult>> {
  const encodedName = encodeURIComponent(celebrityName);
  return apiClient.put<UpdateGlobalDeathResult>(
    `/admin/dead-celebrities/${encodedName}`,
    input
  );
}

// ============================================================================
// Celebrity Cache Management
// ============================================================================

/**
 * Refresh a celebrity's cache by fetching fresh data from API Ninjas
 * 
 * POST /admin/celebrities/{celebrityName}/refresh
 * Implements Requirement 6.4
 * 
 * @param celebrityName - Name of the celebrity to refresh
 * @returns Fresh celebrity data
 */
export function refreshCelebrityCache(
  celebrityName: string
): Promise<ApiResponse<Celebrity>> {
  const encodedName = encodeURIComponent(celebrityName);
  return apiClient.post<Celebrity>(`/admin/celebrities/${encodedName}/refresh`);
}

/**
 * Invalidate a celebrity's cache without fetching new data
 * 
 * DELETE /admin/celebrities/{celebrityName}/cache
 * Implements Requirement 6.5
 * 
 * @param celebrityName - Name of the celebrity to invalidate
 */
export function invalidateCelebrityCache(
  celebrityName: string
): Promise<ApiResponse<void>> {
  const encodedName = encodeURIComponent(celebrityName);
  return apiClient.delete<void>(`/admin/celebrities/${encodedName}/cache`);
}

// ============================================================================
// Custom Celebrity Management
// ============================================================================

/**
 * Update a custom celebrity's details
 * 
 * PUT /admin/custom-celebrities/{celebrityName}
 * Implements Requirement 8.1
 * 
 * @param celebrityName - Current name of the celebrity to update
 * @param input - Fields to update
 * @returns Updated custom celebrity
 */
export function updateCustomCelebrity(
  celebrityName: string,
  input: UpdateCustomCelebrityInput
): Promise<ApiResponse<CustomCelebrityWithStats>> {
  const encodedName = encodeURIComponent(celebrityName);
  return apiClient.put<CustomCelebrityWithStats>(
    `/admin/custom-celebrities/${encodedName}`,
    input
  );
}

/**
 * Merge two custom celebrities
 * Updates all picks and death records to reference the target celebrity
 * 
 * POST /admin/custom-celebrities/merge
 * Implements Requirement 9.1
 * 
 * @param input - Merge input with source and target names
 * @returns The merged celebrity and update counts
 */
export function mergeCustomCelebrities(
  input: MergeCelebritiesInput
): Promise<ApiResponse<MergeCelebritiesResult>> {
  return apiClient.post<MergeCelebritiesResult>(
    '/admin/custom-celebrities/merge',
    input
  );
}

/**
 * Delete a custom celebrity
 * Will fail if celebrity has pick references
 * 
 * DELETE /admin/custom-celebrities/{celebrityName}
 * Implements Requirement 10.1
 * 
 * @param celebrityName - Name of the celebrity to delete
 */
export function deleteCustomCelebrity(
  celebrityName: string
): Promise<ApiResponse<void>> {
  const encodedName = encodeURIComponent(celebrityName);
  return apiClient.delete<void>(`/admin/custom-celebrities/${encodedName}`);
}

/**
 * List all custom celebrities with usage statistics
 * Supports pagination, sorting, and filtering
 * 
 * GET /admin/custom-celebrities
 * Implements Requirement 11.1
 * 
 * @param options - Listing options (pagination, sorting, filtering)
 * @returns Paginated list of custom celebrities with stats
 */
export function listCustomCelebrities(
  options: ListCustomCelebritiesOptions = {}
): Promise<ApiResponse<PaginatedCustomCelebrities>> {
  const params = new URLSearchParams();
  
  if (options.limit !== undefined) {
    params.set('limit', options.limit.toString());
  }
  if (options.offset !== undefined) {
    params.set('offset', options.offset.toString());
  }
  if (options.sortBy) {
    params.set('sortBy', options.sortBy);
  }
  if (options.sortOrder) {
    params.set('sortOrder', options.sortOrder);
  }
  if (options.searchQuery) {
    params.set('search', options.searchQuery);
  }

  const queryString = params.toString();
  const endpoint = queryString 
    ? `/admin/custom-celebrities?${queryString}` 
    : '/admin/custom-celebrities';
    
  return apiClient.get<PaginatedCustomCelebrities>(endpoint);
}

// ============================================================================
// Death Source Management
// ============================================================================

/**
 * List all configured death sources
 * 
 * GET /admin/death-sources
 * Implements Requirement 1.4
 * 
 * @returns Object containing array of death source configurations
 */
export function listDeathSources(): Promise<ApiResponse<{ sources: DeathSource[] }>> {
  return apiClient.get<{ sources: DeathSource[] }>('/admin/death-sources');
}

/**
 * Create a new death source
 * 
 * POST /admin/death-sources
 * Implements Requirement 1.5
 * 
 * @param input - Death source configuration (name, URL, enabled status)
 * @returns Created death source
 */
export function createDeathSource(
  input: CreateDeathSourceInput
): Promise<ApiResponse<DeathSource>> {
  return apiClient.post<DeathSource>('/admin/death-sources', input);
}

/**
 * Update an existing death source
 * 
 * PUT /admin/death-sources/{sourceId}
 * Implements Requirement 1.6
 * 
 * @param sourceId - ID of the death source to update
 * @param input - Fields to update
 * @returns Updated death source
 */
export function updateDeathSource(
  sourceId: string,
  input: UpdateDeathSourceInput
): Promise<ApiResponse<DeathSource>> {
  const encodedId = encodeURIComponent(sourceId);
  return apiClient.put<DeathSource>(`/admin/death-sources/${encodedId}`, input);
}

/**
 * Delete a death source
 * 
 * DELETE /admin/death-sources/{sourceId}
 * Implements Requirement 1.7
 * 
 * @param sourceId - ID of the death source to delete
 */
export function deleteDeathSource(
  sourceId: string
): Promise<ApiResponse<void>> {
  const encodedId = encodeURIComponent(sourceId);
  return apiClient.delete<void>(`/admin/death-sources/${encodedId}`);
}

/**
 * Trigger scraping for a single death source
 * 
 * POST /admin/death-sources/{sourceId}/scrape
 * Implements Requirement 7.7
 * 
 * @param sourceId - ID of the death source to scrape
 * @returns Scraper run result for this source
 */
export function scrapeSource(
  sourceId: string
): Promise<ApiResponse<ScraperRunResult>> {
  const encodedId = encodeURIComponent(sourceId);
  return apiClient.post<ScraperRunResult>(`/admin/death-sources/${encodedId}/scrape`);
}

/**
 * Run the scraper for all enabled death sources
 * 
 * POST /admin/scraper/run
 * Implements Requirement 5.2
 * 
 * @returns Scraper run result with statistics
 */
export function runScraper(): Promise<ApiResponse<ScraperRunResult>> {
  return apiClient.post<ScraperRunResult>('/admin/scraper/run');
}

// ============================================================================
// Pending Death Management
// ============================================================================

/** Result of approving a pending death */
export interface ApprovePendingDeathResult {
  record: PendingDeathRecord;
  globalDeathResult: {
    poolsUpdated: number;
    playersAffected: number;
  };
}

/**
 * List pending death records with optional status filter
 * 
 * GET /admin/pending-deaths?status={status}
 * Implements Requirement 4.6
 * 
 * @param status - Optional status filter (pending, approved, rejected)
 * @returns Array of pending death records
 */
export function listPendingDeaths(
  status?: PendingDeathStatus
): Promise<ApiResponse<{ records: PendingDeathRecord[]; total: number }>> {
  const endpoint = status 
    ? `/admin/pending-deaths?status=${encodeURIComponent(status)}`
    : '/admin/pending-deaths';
  return apiClient.get<{ records: PendingDeathRecord[]; total: number }>(endpoint);
}

/**
 * Approve a pending death record
 * Adds the celebrity to the global dead list
 * 
 * POST /admin/pending-deaths/{recordId}/approve
 * Implements Requirement 4.8
 * 
 * @param recordId - ID of the pending death record to approve
 * @param input - Approval input with age at death and optional source URL override
 * @returns Approved record and global death result
 */
export function approvePendingDeath(
  recordId: string,
  input: ApprovePendingDeathInput
): Promise<ApiResponse<ApprovePendingDeathResult>> {
  const encodedId = encodeURIComponent(recordId);
  return apiClient.post<ApprovePendingDeathResult>(
    `/admin/pending-deaths/${encodedId}/approve`,
    input
  );
}

/**
 * Reject a pending death record
 * 
 * POST /admin/pending-deaths/{recordId}/reject
 * Implements Requirement 4.9
 * 
 * @param recordId - ID of the pending death record to reject
 * @param input - Rejection input with reason
 * @returns Rejected record
 */
export function rejectPendingDeath(
  recordId: string,
  input: RejectPendingDeathInput
): Promise<ApiResponse<PendingDeathRecord>> {
  const encodedId = encodeURIComponent(recordId);
  return apiClient.post<PendingDeathRecord>(
    `/admin/pending-deaths/${encodedId}/reject`,
    input
  );
}
